import { useState, useRef, useCallback, useEffect } from 'react';
import { needsKeyboardViewportCompensation, resolveKeyboardMenuPlacement, onKeyboardSettle, scrollIntoKeyboardView } from '../methods/keyboardViewport.js';

const FIELD_INPUT_SELECTOR = 'input:not([type="hidden"])';
const noopFocus = () => { };
/**
 * iOS: keeps a focused field above the keyboard and resolves dropdown direction
 * from the visible viewport.
 *
 * Android: returns inert handlers — the browser already handles keyboard scroll.
 */
function useKeyboardAwareField(anchorRef, { enabled = true, scrollOnFocus = true, menuMaxHeight, defaultMenuPlacement = "bottom", clearance, } = {}) {
    const active = enabled && needsKeyboardViewportCompensation();
    const [menuPlacement, setMenuPlacement] = useState(defaultMenuPlacement);
    const cleanupRef = useRef(null);
    const getFieldElement = useCallback(() => {
        var _a, _b;
        const anchor = anchorRef.current;
        return ((_b = (_a = anchor === null || anchor === void 0 ? void 0 : anchor.querySelector(FIELD_INPUT_SELECTOR)) !== null && _a !== void 0 ? _a : anchor) !== null && _b !== void 0 ? _b : null);
    }, [anchorRef]);
    const syncPlacement = useCallback(() => {
        const element = getFieldElement();
        if (!active || !menuMaxHeight || !element) {
            return;
        }
        setMenuPlacement(resolveKeyboardMenuPlacement(element, {
            maxHeight: menuMaxHeight,
            clearance,
        }));
    }, [active, clearance, getFieldElement, menuMaxHeight]);
    const stopTracking = useCallback(() => {
        var _a;
        (_a = cleanupRef.current) === null || _a === void 0 ? void 0 : _a.call(cleanupRef);
        cleanupRef.current = null;
    }, []);
    const onFieldFocus = useCallback((event) => {
        if (!active || !(event.target instanceof Element)) {
            return;
        }
        const target = event.target;
        syncPlacement();
        stopTracking();
        cleanupRef.current = onKeyboardSettle(() => {
            if (scrollOnFocus) {
                scrollIntoKeyboardView(target, { clearance });
            }
            syncPlacement();
        });
    }, [active, clearance, scrollOnFocus, stopTracking, syncPlacement]);
    useEffect(() => stopTracking, [stopTracking]);
    if (!active) {
        return {
            menuPlacement: defaultMenuPlacement,
            onFieldFocus: noopFocus,
            onFieldBlur: noopFocus,
            syncPlacement: noopFocus,
        };
    }
    return {
        menuPlacement,
        onFieldFocus,
        onFieldBlur: stopTracking,
        syncPlacement,
    };
}

export { useKeyboardAwareField };
//# sourceMappingURL=useKeyboardAwareField.js.map
